import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
    Wrench, ChevronDown, ChevronRight, Trash2, AlertCircle,
    Calendar, Shield, Wrench as Tool, Clock, ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { getRepairLogs, deleteRepairLog, clearAllLogs } from '../lib/repairLogs';
import { toast } from 'sonner';

export default function LogsPage() {
    const navigate = useNavigate();
    const [logs, setLogs] = useState([]);
    const [expandedLog, setExpandedLog] = useState(null);

    useEffect(() => {
        loadLogs();
    }, []);

    const loadLogs = () => {
        const repairLogs = getRepairLogs();
        setLogs(repairLogs);
    };

    const handleDelete = (id, e) => {
        e.stopPropagation();
        if (window.confirm('Are you sure you want to delete this repair log?')) {
            if (deleteRepairLog(id)) {
                toast.success('Log deleted successfully');
                loadLogs();
                if (expandedLog === id) setExpandedLog(null);
            } else {
                toast.error('Failed to delete log');
            }
        }
    };

    const handleClearAll = () => {
        if (window.confirm('Are you sure you want to delete ALL repair logs? This cannot be undone.')) {
            if (clearAllLogs()) {
                toast.success('All logs cleared');
                setLogs([]);
                setExpandedLog(null);
            } else {
                toast.error('Failed to clear logs');
            }
        }
    };

    const formatDate = (isoString) => {
        const date = new Date(isoString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getSafetyColor = (risk) => {
        switch (risk) {
            case 'high': return 'text-destructive';
            case 'medium': return 'text-warning';
            case 'low': return 'text-success';
            default: return 'text-text-muted';
        }
    };

    return (
        <div className="min-h-screen bg-background grid-pattern">
            {/* Header */}
            <header className="border-b border-border bg-surface/80 backdrop-blur-sm sticky top-0 z-40">
                <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div
                            className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => navigate('/')}
                        >
                            <div className="w-10 h-10 bg-primary flex items-center justify-center">
                                <ArrowLeft className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="font-heading text-xl font-bold uppercase tracking-tight text-white">
                                    Repair History
                                </h1>
                                <span className="font-mono text-xs text-text-muted hidden sm:inline-block">
                                    {logs.length} {logs.length === 1 ? 'repair' : 'repairs'} completed
                                </span>
                            </div>
                        </div>

                        {logs.length > 0 && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleClearAll}
                                className="border-destructive text-destructive hover:bg-destructive/10"
                            >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Clear All
                            </Button>
                        )}
                    </div>
                </div>
            </header>

            {/* Main content */}
            <main className="w-full px-4 sm:px-6 lg:px-8 py-8 max-w-4xl mx-auto">
                {logs.length === 0 ? (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center py-20"
                    >
                        <div className="w-20 h-20 bg-surface border border-border rounded-full flex items-center justify-center mx-auto mb-6">
                            <Wrench className="w-10 h-10 text-text-muted" />
                        </div>
                        <h2 className="font-heading text-2xl font-bold text-white mb-2">No Repair History Yet</h2>
                        <p className="text-text-muted mb-6">
                            Complete your first repair to see it logged here.
                        </p>
                        <Button
                            onClick={() => navigate('/')}
                            className="bg-primary text-white hover:bg-primary/90 font-heading uppercase"
                        >
                            Start a Repair
                        </Button>
                    </motion.div>
                ) : (
                    <div className="space-y-4">
                        {logs.map((log, index) => {
                            const isExpanded = expandedLog === log.id;
                            return (
                                <motion.div
                                    key={log.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                    className="bg-surface border border-border hover:border-primary/50 transition-colors"
                                >
                                    {/* Log header - clickable */}
                                    <div
                                        className="p-4 sm:p-6 cursor-pointer"
                                        onClick={() => setExpandedLog(isExpanded ? null : log.id)}
                                    >
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <h3 className="font-heading text-lg font-bold text-white truncate">
                                                        {log.deviceInfo?.device_type || 'Unknown Device'}
                                                        {log.deviceInfo?.brand && log.deviceInfo.brand !== 'Unknown' && (
                                                            <span className="text-text-muted font-normal"> - {log.deviceInfo.brand}</span>
                                                        )}
                                                    </h3>
                                                </div>
                                                <div className="flex flex-wrap items-center gap-3 text-xs text-text-muted">
                                                    <span className="flex items-center gap-1">
                                                        <Calendar className="w-3 h-3" />
                                                        {formatDate(log.completedAt)}
                                                    </span>
                                                    {log.diagnosis?.safety_risk && (
                                                        <span className={`flex items-center gap-1 ${getSafetyColor(log.diagnosis.safety_risk)}`}>
                                                            <Shield className="w-3 h-3" />
                                                            {log.diagnosis.safety_risk} risk
                                                        </span>
                                                    )}
                                                    {log.repairSteps && (
                                                        <span className="flex items-center gap-1">
                                                            <Tool className="w-3 h-3" />
                                                            {log.repairSteps.length} steps
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={(e) => handleDelete(log.id, e)}
                                                    className="text-destructive hover:bg-destructive/10"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                                {isExpanded ? (
                                                    <ChevronDown className="w-5 h-5 text-primary" />
                                                ) : (
                                                    <ChevronRight className="w-5 h-5 text-text-muted" />
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Expanded details */}
                                    {isExpanded && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            className="border-t border-border p-4 sm:p-6 space-y-4"
                                        >
                                            {/* Diagnosis */}
                                            {log.diagnosis && (
                                                <div>
                                                    <h4 className="font-heading text-sm uppercase text-primary mb-2">Diagnosis</h4>
                                                    <p className="text-text-secondary text-sm leading-relaxed mb-2">
                                                        {log.diagnosis.diagnosis}
                                                    </p>
                                                    {log.diagnosis.broken_part && (
                                                        <p className="text-xs text-text-muted">
                                                            <span className="font-semibold">Broken Part:</span> {log.diagnosis.broken_part}
                                                        </p>
                                                    )}
                                                </div>
                                            )}

                                            {/* Tools Required */}
                                            {log.diagnosis?.tools_required && log.diagnosis.tools_required.length > 0 && (
                                                <div>
                                                    <h4 className="font-heading text-sm uppercase text-primary mb-2">Tools Required</h4>
                                                    <div className="flex flex-wrap gap-2">
                                                        {log.diagnosis.tools_required.map((tool, idx) => (
                                                            <span
                                                                key={idx}
                                                                className="px-2 py-1 bg-surface border border-border text-xs text-white"
                                                            >
                                                                {tool}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Safety Notice */}
                                            {log.safetyCheck?.safety_notice && (
                                                <div className="bg-warning/10 border border-warning/30 p-3">
                                                    <div className="flex items-start gap-2">
                                                        <AlertCircle className="w-4 h-4 text-warning mt-0.5 flex-shrink-0" />
                                                        <div>
                                                            <h4 className="font-heading text-sm uppercase text-warning mb-1">Safety Notice</h4>
                                                            <p className="text-xs text-text-secondary">{log.safetyCheck.safety_notice}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Repair Steps Summary */}
                                            {log.repairSteps && log.repairSteps.length > 0 && (
                                                <div>
                                                    <h4 className="font-heading text-sm uppercase text-primary mb-2">
                                                        Repair Steps ({log.repairSteps.length})
                                                    </h4>
                                                    <div className="space-y-2">
                                                        {log.repairSteps.map((step, idx) => (
                                                            <div key={idx} className="flex gap-2 text-sm">
                                                                <span className="text-primary font-mono">{idx + 1}.</span>
                                                                <span className="text-text-secondary">{step.instruction}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </motion.div>
                                    )}
                                </motion.div>
                            );
                        })}
                    </div>
                )}
            </main>
        </div>
    );
}
